import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Api } from "../api/client";
import {
  Wand2, RefreshCw, Sparkles, FileText, CheckCircle2,
  AlertCircle, Image as ImageIcon, ExternalLink,
} from "lucide-react";
import { format, addDays, parseISO } from "date-fns";

const SIGN_NAMES: Record<string, string> = {
  aries: "Овен", taurus: "Телец", gemini: "Близнецы", cancer: "Рак",
  leo: "Лев", virgo: "Дева", libra: "Весы", scorpio: "Скорпион",
  sagittarius: "Стрелец", capricorn: "Козерог", aquarius: "Водолей", pisces: "Рыбы",
};

const CHANNEL_NAMES: Record<string, string> = {
  telegram: "Telegram",
  vk: "VKontakte",
  ok: "Одноклассники",
  max: "MAX",
};

export function ContentByDay() {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(format(today, "yyyy-MM-dd"));
  const queryClient = useQueryClient();

  const content = useQuery({
    queryKey: ["content-status", selectedDate],
    queryFn: () => Api.contentStatus(selectedDate),
    retry: 1,
  });
  const publications = useQuery({
    queryKey: ["publications", selectedDate],
    queryFn: () => Api.publications(selectedDate),
    retry: 1,
  });

  const mGenerate = useMutation({
    mutationFn: () => Api.triggerGenerate(selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["content-status", selectedDate] });
      queryClient.invalidateQueries({ queryKey: ["publications", selectedDate] });
    },
  });
  const mSignForecasts = useMutation({
    mutationFn: () => Api.triggerSignForecasts(selectedDate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["content-status", selectedDate] }),
  });
  const mBuildDzen = useMutation({
    mutationFn: () => Api.triggerBuildDzen(selectedDate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["content-status", selectedDate] }),
  });
  const mPublish = useMutation({
    mutationFn: (channels?: string) => Api.triggerPublish(selectedDate, channels),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["publications", selectedDate] }),
  });
  // Combined-режим: публикуем ровно один объединённый пост дня (sign_code='all').
  const mPublishDzen = useMutation({
    mutationFn: () => Api.triggerPublish(selectedDate, "dzen", "all"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["publications", selectedDate] }),
  });

  const shift = (days: number) =>
    setSelectedDate(format(addDays(parseISO(selectedDate), days), "yyyy-MM-dd"));

  // быстрые сводки
  const signsReady = content.data?.signDayCore.filter(s => s.hasContent).length ?? 0;
  const combined = content.data?.combined;
  const bundleReady = content.data?.socialBundle?.hasContent ?? false;

  const dzenPubs = publications.data?.dzen ?? [];
  const socialPubs = publications.data?.social ?? [];
  const combinedPub = dzenPubs.find(p => p.signCode === "all");
  const combinedPublished = (combined?.published ?? false) || combinedPub?.status === "published";
  const combinedPostUrl = combined?.postUrl ?? combinedPub?.postUrl ?? null;
  const socialPublishedCount = socialPubs.filter(p => (p.status ?? "").toLowerCase().includes("publish")).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Контент</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="btn-secondary">←</button>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          <button onClick={() => shift(1)} className="btn-secondary">→</button>
          <button onClick={() => setSelectedDate(format(new Date(), "yyyy-MM-dd"))}
            className="btn-secondary">Сегодня</button>
        </div>
      </div>

      {/* Сводка */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          label="Sign Day Core"
          value={`${signsReady} / 12`}
          subtitle="прогнозы знаков"
          ok={signsReady === 12}
        />
        <SummaryCard
          label="Дзен — пост дня"
          value={combined?.hasArticle ? "готов" : "—"}
          subtitle={
            combined?.hasLanding
              ? (combined?.hasCover ? "AI-крючок + обложка" : "AI-крючок, без обложки")
              : "без AI-landing"
          }
          ok={(combined?.hasArticle ?? false) && (combined?.hasLanding ?? false) && (combined?.hasCover ?? false)}
        />
        <SummaryCard
          label="Соц.bundle"
          value={bundleReady ? "готов" : "—"}
          subtitle={content.data?.socialBundle?.hasCover ? "с обложкой" : "без обложки"}
          ok={bundleReady && (content.data?.socialBundle?.hasCover ?? false)}
        />
        <SummaryCard
          label="Опубликовано"
          value={`Дзен ${combinedPublished ? "1/1" : "0/1"} · Соц ${socialPublishedCount}/4`}
          subtitle={publications.isLoading ? "загружаем…" : "1 пост дня + 4 соцсети"}
          ok={combinedPublished && socialPublishedCount === 4}
        />
      </section>

      {/* Триггеры */}
      <section className="card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Триггеры</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ActionCard
            icon={<Sparkles size={18} />}
            title="Sign forecasts"
            description="Pipeline1 — 12 знаков, ~13 мин"
            running={mSignForecasts.isPending}
            onClick={() => mSignForecasts.mutate()}
          />
          <ActionCard
            icon={<Wand2 size={18} />}
            title="Generate (bundle)"
            description="bundle + обложка для соц.сетей"
            running={mGenerate.isPending}
            onClick={() => mGenerate.mutate()}
          />
          <ActionCard
            icon={<FileText size={18} />}
            title="Build Дзен"
            description="bundle + landing → 1 пост дня"
            running={mBuildDzen.isPending}
            onClick={() => mBuildDzen.mutate()}
          />
          <ActionCard
            icon={<RefreshCw size={18} />}
            title="Publish (всё)"
            description="TG/VK/OK/MAX + Дзен"
            running={mPublish.isPending}
            onClick={() => mPublish.mutate(undefined)}
          />
        </div>
      </section>

      {/* Sign Day Core */}
      <section className="card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Sign Day Core (Pipeline1)</h2>
        {content.isLoading && <p className="text-white/40 text-sm">Загружаем…</p>}
        {content.data && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {content.data.signDayCore.map(s => (
              <div key={s.signCode} className={`rounded-lg px-3 py-2 flex items-center gap-2 ${
                s.hasContent ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-rose-500/5 border border-rose-500/20"
              }`}>
                {s.hasContent
                  ? <CheckCircle2 size={14} className="text-emerald-300 shrink-0" />
                  : <AlertCircle size={14} className="text-rose-300 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium">{SIGN_NAMES[s.signCode] ?? s.signCode}</div>
                  {s.archetype && (
                    <div className="text-xs text-white/40 truncate" title={s.archetype}>
                      {s.archetype}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Дзен — объединённый пост дня (combined-режим: 1 пост/день с AI-крючком) */}
      <section className="card p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-semibold">Дзен — пост дня</h2>
            <p className="text-xs text-white/40 mt-0.5">1 объединённый пост (все знаки) с AI-заголовком и тематической обложкой</p>
          </div>
          <button
            onClick={() => mPublishDzen.mutate()}
            disabled={mPublishDzen.isPending || !combined?.hasArticle}
            className="btn-secondary text-sm disabled:opacity-40"
          >
            {mPublishDzen.isPending
              ? <><RefreshCw size={14} className="animate-spin inline mr-1" />Публикуем…</>
              : combinedPublished ? "Опубликовать заново" : "Опубликовать пост дня"}
          </button>
        </div>
        {!combined?.hasArticle ? (
          <p className="text-white/40 text-sm">
            Объединённый пост ещё не собран. Сгенерируй контент дня (bundle + sign forecasts) —
            AI-landing и пост соберутся автоматически в backfill.
          </p>
        ) : (
          <div className="bg-black/20 rounded-lg p-4 border border-white/5 space-y-2">
            {combined.landingTheme && (
              <div className="text-xs uppercase tracking-wider text-astro-violet/80">{combined.landingTheme}</div>
            )}
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-lg font-semibold leading-snug">
                {combined.title ?? combined.landingTitle ?? "—"}
              </h3>
              <span className="shrink-0 text-xs">
                {combinedPublished
                  ? <span className="badge-ok">опубликовано</span>
                  : combined.hasCover
                  ? <span className="badge-pending">готов</span>
                  : <span className="badge-pending">без обложки</span>}
              </span>
            </div>
            {combined.landingIntro && (
              <p className="text-sm text-white/60 line-clamp-3">{combined.landingIntro}</p>
            )}
            {combined.landingOutro && (
              <p className="text-xs text-white/40 italic line-clamp-2">↳ {combined.landingOutro}</p>
            )}
            <div className="flex items-center gap-3 pt-1 flex-wrap text-xs">
              <span className={`flex items-center gap-1 ${combined.hasLanding ? "text-emerald-300" : "text-rose-300"}`}>
                {combined.hasLanding ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} AI-landing
              </span>
              <span className={`flex items-center gap-1 ${combined.hasCover ? "text-emerald-300" : "text-white/30"}`}>
                <ImageIcon size={12} /> {combined.hasCover ? "обложка 16:9" : "без обложки"}
              </span>
              {combinedPostUrl && (
                <a href={combinedPostUrl} target="_blank" rel="noreferrer"
                   className="text-astro-violet hover:underline flex items-center gap-1 ml-auto">
                  <ExternalLink size={11} /> открыть пост
                </a>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Социалки */}
      <section className="card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Социальный контент</h2>
        {content.data?.socialBundle && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {content.data.socialBundle.hasContent
                ? <CheckCircle2 size={16} className="text-emerald-300" />
                : <AlertCircle size={16} className="text-rose-300" />}
              <span className="font-medium">
                {content.data.socialBundle.hasContent ? "Bundle готов" : "Bundle не готов"}
              </span>
              {content.data.socialBundle.hasCover && (
                <span className="text-xs text-astro-violet flex items-center gap-1">
                  <ImageIcon size={11} /> с обложкой
                </span>
              )}
            </div>
            {content.data.socialBundle.title && (
              <div className="text-sm text-white/70">{content.data.socialBundle.title}</div>
            )}
            {content.data.socialBundle.intro && (
              <div className="text-xs text-white/50 line-clamp-2">{content.data.socialBundle.intro}</div>
            )}
            {socialPubs.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                {["telegram", "vk", "ok", "max"].map(channel => {
                  const pub = socialPubs.find(p => p.channelCode?.toLowerCase() === channel);
                  return (
                    <div key={channel} className="bg-black/20 rounded px-3 py-2">
                      <div className="text-xs text-white/40">{CHANNEL_NAMES[channel]}</div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        {pub?.status && (pub.status.toLowerCase().includes("publish")) ? (
                          pub.externalUrl ? (
                            <a href={pub.externalUrl} target="_blank" rel="noreferrer"
                               className="text-xs text-astro-violet hover:underline truncate flex-1">
                              опубликовано <ExternalLink size={11} className="inline ml-1" />
                            </a>
                          ) : (
                            <span className="text-xs text-emerald-300">опубликовано</span>
                          )
                        ) : pub?.status?.toLowerCase().includes("fail") ? (
                          <span className="text-xs text-rose-300">ошибка</span>
                        ) : (
                          <span className="text-xs text-white/30">не опубликовано</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  subtitle: string;
  ok: boolean;
}

function SummaryCard({ label, value, subtitle, ok }: SummaryCardProps) {
  return (
    <div className={`card p-4 border ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10"}`}>
      <div className="text-xs text-white/40 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
      {subtitle && <div className="text-xs text-white/50 mt-1">{subtitle}</div>}
    </div>
  );
}

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  running: boolean;
  onClick: () => void;
}

function ActionCard({ icon, title, description, running, onClick }: ActionCardProps) {
  return (
    <button
      onClick={onClick}
      disabled={running}
      className="text-left p-4 bg-black/20 hover:bg-black/30 border border-white/10 rounded-xl transition disabled:opacity-50"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-astro-violet">{icon}</span>
        <span className="font-medium">{title}</span>
        {running && <RefreshCw size={14} className="ml-auto animate-spin text-white/40" />}
      </div>
      <p className="text-xs text-white/50">{description}</p>
    </button>
  );
}
